import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getOrganization,
  getOrganizationSubscription,
  updateOrganization,
  addCompanyToOrg,
  upgradeOrganizationPlan,
} from "../api/organizations";
import CompanyAvatar from "../components/CompanyAvatar";
import PhoneInput from "../components/PhoneInput";

const PLAN_COLORS = {
  FREE: "bg-gray-100 text-gray-700",
  STANDARD: "bg-blue-100 text-blue-700",
  STANDARD_IN: "bg-blue-100 text-blue-700",
  BUSINESS: "bg-purple-100 text-purple-700",
  BUSINESS_IN: "bg-purple-100 text-purple-700",
  BUSINESS_PRO: "bg-indigo-100 text-indigo-700",
  BUSINESS_PRO_IN: "bg-indigo-100 text-indigo-700",
  GROUP_STARTER: "bg-emerald-100 text-emerald-700",
  GROUP_STARTER_IN: "bg-emerald-100 text-emerald-700",
  GROUP_PRO: "bg-teal-100 text-teal-700",
  GROUP_PRO_IN: "bg-teal-100 text-teal-700",
  ENTERPRISE_HOLDING: "bg-orange-100 text-orange-700",
  ENTERPRISE_HOLDING_IN: "bg-orange-100 text-orange-700",
};

const ACTIVITY_ICONS = {
  SUBSCRIPTION_CREATED: "✅",
  SUBSCRIPTION_UPDATED: "🔄",
  SUBSCRIPTION_CANCELLED: "❌",
  SUBSCRIPTION_DELETED: "🗑️",
  PAYMENT_SUCCEEDED: "💰",
  PAYMENT_FAILED: "⚠️",
  INVOICE_PAID: "🧾",
  INVOICE_PAYMENT_FAILED: "⛔",
  CHECKOUT_COMPLETED: "🛒",
  PLAN_CHANGED: "📋",
  USAGE_RESET: "🔃",
  COMPANY_ADDED: "🏢",
};

const TABS = ["Overview", "Companies", "Members", "Subscription", "Activity"];

const OrganizationDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("Overview");
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [addCompanyForm, setAddCompanyForm] = useState({
    name: "",
    phone: "",
    address: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeTargetPlan, setUpgradeTargetPlan] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => getOrganization(orgId).then((r) => r.data.data),
    enabled: !!orgId,
  });

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ["organization-subscription", orgId],
    queryFn: () => getOrganizationSubscription(orgId).then((r) => r.data.data),
    enabled: !!orgId && activeTab === "Subscription",
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updateOrganization(orgId, payload),
    onSuccess: () => {
      toast.success("Organization updated");
      queryClient.invalidateQueries(["organization", orgId]);
      setEditMode(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to update organization",
      );
    },
  });

  const addCompanyMutation = useMutation({
    mutationFn: (payload) => addCompanyToOrg(orgId, payload),
    onSuccess: () => {
      toast.success("Company added to organization");
      queryClient.invalidateQueries(["organization", orgId]);
      setShowAddCompanyModal(false);
      setAddCompanyForm({ name: "", phone: "", address: "" });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to add company");
    },
  });

  const upgradePlanMutation = useMutation({
    mutationFn: (payload) => upgradeOrganizationPlan(orgId, payload),
    onSuccess: (_, payload) => {
      toast.success(
        `Upgraded to ${payload.targetPlan === "BUSINESS_PRO" ? "Business Pro" : "Enterprise Holding"} successfully`,
      );
      queryClient.invalidateQueries(["organization", orgId]);
      queryClient.invalidateQueries(["organization-subscription", orgId]);
      setShowUpgradeModal(false);
      setUpgradeTargetPlan(null);
      setUpgradeForm(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to upgrade plan");
    },
  });

  const openUpgradeModal = (targetPlan) => {
    const org = data?.organization;
    const pm = org?.planMeta || {};
    const isEnterprise = targetPlan === "ENTERPRISE_HOLDING";
    setUpgradeTargetPlan(targetPlan);
    setUpgradeForm({
      subscriptionType: org?.subscriptionType || "MONTHLY",
      monthlyPrice: pm.MONTHLY ?? "",
      yearlyPrice: pm.YEARLY ?? "",
      planMeta: {
        USER_CREATION: pm.USER_CREATION ?? -1,
        SIGN_REQUESTS: pm.SIGN_REQUESTS ?? 30,
        TEMPLATE_CREATION: pm.TEMPLATE_CREATION ?? -1,
        BULK_SEND: pm.BULK_SEND ?? true,
        REMINDERS: pm.REMINDERS ?? true,
        MULTI_DEVICE_ACCESS: pm.MULTI_DEVICE_ACCESS ?? true,
        SEQUENTIAL_SIGNING: pm.SEQUENTIAL_SIGNING ?? true,
        ROLE_CONTROL: pm.ROLE_CONTROL ?? true,
        ATTACHMENT_REQUESTS: pm.ATTACHMENT_REQUESTS ?? true,
        DEDICATED_SUPPORT: pm.DEDICATED_SUPPORT ?? true,
        OTP_VERIFICATION: pm.OTP_VERIFICATION ?? true,
        ALLOWED_COMPANIES: isEnterprise ? -1 : 1,
        IS_GROUP_PLAN: isEnterprise,
      },
    });
    setShowUpgradeModal(true);
  };

  const setUpgradeField = (key, value) =>
    setUpgradeForm((f) => (f ? { ...f, [key]: value } : f));

  const setUpgradePlanMetaField = (key, value) =>
    setUpgradeForm((f) =>
      f ? { ...f, planMeta: { ...f.planMeta, [key]: value } } : f,
    );

  const handleUpgradeSubmit = () => {
    if (!upgradeForm || !upgradeTargetPlan) return;
    const price =
      upgradeForm.subscriptionType === "MONTHLY"
        ? upgradeForm.monthlyPrice
        : upgradeForm.yearlyPrice;
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      toast.error("A valid price is required for the selected billing cycle.");
      return;
    }
    const payload = {
      targetPlan: upgradeTargetPlan,
      planMeta: upgradeForm.planMeta,
      subscriptionType: upgradeForm.subscriptionType,
    };
    if (upgradeForm.subscriptionType === "MONTHLY") {
      payload.monthlyPrice = parseFloat(upgradeForm.monthlyPrice);
    } else {
      payload.yearlyPrice = parseFloat(upgradeForm.yearlyPrice);
    }
    upgradePlanMutation.mutate(payload);
  };

  const org = data?.organization;
  const hasActiveSubscription =
    org?.isSubscribed ||
    org?.stripeSubscriptionId ||
    org?.razorpaySubscriptionId;
  const isUpgradeable =
    hasActiveSubscription &&
    ![
      "BUSINESS_PRO",
      "BUSINESS_PRO_IN",
      "ENTERPRISE_HOLDING",
      "ENTERPRISE_HOLDING_IN",
    ].includes(org?.planId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading organization details...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Failed to load organization.{" "}
          <button onClick={() => navigate(-1)} className="underline">
            Go back
          </button>
        </div>
      </div>
    );
  }

  const {
    organization,
    companies,
    memberships,
    activities,
    stripeSubscription,
  } = data;

  const pm = organization.planMeta || {};
  const startEdit = () => {
    setEditForm({
      name: organization.name,
      planId: organization.planId,
      isSubscribed: organization.isSubscribed,
      payment_country: organization.payment_country || "US",
      payment_gateway: organization.payment_gateway || "STRIPE",
      subscriptionType: organization.subscriptionType || "MONTHLY",
      allowedCompanies: organization.allowedCompanies,
      allowedSignRequests: organization.allowedSignRequests,
      allowedTemplateCreation: organization.allowedTemplateCreation,
      allowBulkSend: organization.allowBulkSend,
      planMeta: {
        USER_CREATION: pm.USER_CREATION ?? -1,
        SIGN_REQUESTS: pm.SIGN_REQUESTS ?? 0,
        TEMPLATE_CREATION: pm.TEMPLATE_CREATION ?? -1,
        BULK_SEND: pm.BULK_SEND ?? false,
        REMINDERS: pm.REMINDERS ?? false,
        MULTI_DEVICE_ACCESS: pm.MULTI_DEVICE_ACCESS ?? false,
        SEQUENTIAL_SIGNING: pm.SEQUENTIAL_SIGNING ?? false,
        ROLE_CONTROL: pm.ROLE_CONTROL ?? false,
        ATTACHMENT_REQUESTS: pm.ATTACHMENT_REQUESTS ?? false,
        DEDICATED_SUPPORT: pm.DEDICATED_SUPPORT ?? false,
        OTP_VERIFICATION: pm.OTP_VERIFICATION ?? false,
        ALLOWED_COMPANIES: pm.ALLOWED_COMPANIES ?? 1,
        IS_GROUP_PLAN: pm.IS_GROUP_PLAN ?? false,
        MONTHLY: pm.MONTHLY ?? "",
        YEARLY: pm.YEARLY ?? "",
      },
    });
    setEditMode(true);
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate("/dashboard/organizations")}
          className="hover:text-primary"
        >
          Organizations
        </button>
        <span>›</span>
        <span className="text-gray-900 font-medium">{organization.name}</span>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
              {organization.name[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {organization.name}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Owner:{" "}
                <span className="font-medium text-gray-700">
                  {organization.ownerId?.name}
                </span>{" "}
                ({organization.ownerId?.email})
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[organization.planId] || "bg-gray-100 text-gray-600"}`}
                >
                  {organization.planId || "No Plan"}
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${organization.isSubscribed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                >
                  {organization.isSubscribed ? "Subscribed" : "Not Subscribed"}
                </span>
                {organization.lastPaymentFailed && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    Payment Failed
                  </span>
                )}
                {organization.subscriptionCancelled && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    Cancelled
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isUpgradeable && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => openUpgradeModal("BUSINESS_PRO")}
                  disabled={upgradePlanMutation.isPending}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                >
                  Upgrade to Business Pro
                </button>
                <button
                  onClick={() => openUpgradeModal("ENTERPRISE_HOLDING")}
                  disabled={upgradePlanMutation.isPending}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-50 disabled:opacity-50"
                >
                  Upgrade to Enterprise
                </button>
              </div>
            )}
            <button
              onClick={startEdit}
              className="text-sm text-primary hover:text-primary/80 font-medium border border-primary/30 px-3 py-1.5 rounded-lg hover:bg-primary/5"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-4 mt-5 pt-5 border-t">
          {[
            {
              label: "Companies",
              value: `${companies.length} / ${organization.allowedCompanies === -1 ? "∞" : organization.allowedCompanies}`,
            },
            { label: "Members", value: memberships.length },
            {
              label: "Sign Requests Used",
              value: `${organization.usedAllowedSignRequests} / ${organization.allowedSignRequests === -1 ? "∞" : organization.allowedSignRequests}`,
            },
            {
              label: "Templates Used",
              value: `${organization.usedAllowedTemplateCreation} / ${organization.allowedTemplateCreation === -1 ? "∞" : organization.allowedTemplateCreation}`,
            },
          ].map(({ label, value }) => (
            <div key={label} className="text-center">
              <p className="text-xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-6">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}

      {/* Overview Tab */}
      {activeTab === "Overview" && (
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border p-5 space-y-3">
            <h3 className="font-semibold text-gray-800">
              Organization Details
            </h3>
            {[
              ["ID", organization._id],
              ["Created", new Date(organization.createdAt).toLocaleString()],
              ["Payment Country", organization.payment_country],
              ["Payment Gateway", organization.payment_gateway],
              ["Subscription Type", organization.subscriptionType || "—"],
              [
                "Next Usage Reset",
                organization.nextUsageResetAt
                  ? new Date(organization.nextUsageResetAt).toLocaleDateString()
                  : "—",
              ],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-800 text-right">
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-5 space-y-3">
            <h3 className="font-semibold text-gray-800">Billing IDs</h3>
            {[
              ["Stripe Customer", organization.stripeCustomerId || "—"],
              ["Stripe Subscription", organization.stripeSubscriptionId || "—"],
              ["Razorpay Customer", organization.razorpayCustomerId || "—"],
              [
                "Razorpay Subscription",
                organization.razorpaySubscriptionId || "—",
              ],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between text-sm gap-2">
                <span className="text-gray-500 shrink-0">{label}</span>
                <span className="font-mono text-xs text-gray-700 text-right truncate">
                  {value}
                </span>
              </div>
            ))}
            {organization.pendingCheckoutUrl && (
              <a
                href={organization.pendingCheckoutUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline block"
              >
                {organization.payment_gateway === "RAZORPAY"
                  ? "Open Razorpay Payment Link →"
                  : "Open Stripe Checkout →"}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Companies Tab */}
      {activeTab === "Companies" && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold text-gray-800">
              Companies ({companies.length})
            </h3>
            {(organization.allowedCompanies === -1 ||
              companies.length < organization.allowedCompanies) && (
              <button
                onClick={() => setShowAddCompanyModal(true)}
                className="text-sm text-primary hover:text-primary/80 font-medium border border-primary/30 px-3 py-1.5 rounded-lg hover:bg-primary/5"
              >
                + Add Company
              </button>
            )}
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["", "Company Name", "Phone", "Address", "Created", ""].map(
                  (h) => (
                    <th
                      key={h || "logo"}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-400">
                    No companies
                  </td>
                </tr>
              ) : (
                companies.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 w-12">
                      <CompanyAvatar company={c} size="sm" />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {c.name}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {c.phone || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {c.address || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {c.createdAt
                        ? new Date(c.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/companies/${c._id}`}
                        className="text-sm text-primary hover:text-primary/80 font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === "Members" && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-800">
              Members ({memberships.length})
            </h3>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["User", "Company", "Role", "Status", "Joined"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {memberships.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-400">
                    No members
                  </td>
                </tr>
              ) : (
                memberships.map((m) => (
                  <tr key={m._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {m.userId?.name || "—"}
                      </p>
                      <p className="text-xs text-gray-400">{m.userId?.email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {m.companyId?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {m.role?.name || m.companyRole || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {m.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {m.createdAt
                        ? new Date(m.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Subscription Tab — fetches fresh from Stripe/Razorpay every time */}
      {activeTab === "Subscription" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Live data fetched from payment platforms
            </p>
            <button
              onClick={() => refetchSubscription()}
              disabled={subscriptionLoading}
              className="px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 disabled:opacity-50"
            >
              {subscriptionLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {subscriptionLoading ? (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-400">
              Loading subscription details…
            </div>
          ) : (
            <>
              {/* Stripe */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Stripe</h3>
                {subscriptionData?.stripeSubscription?.error ? (
                  <p className="text-sm text-red-600">
                    {subscriptionData.stripeSubscription.error}
                  </p>
                ) : subscriptionData?.stripeSubscription ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                      {[
                        [
                          "Subscription ID",
                          subscriptionData.stripeSubscription.id,
                        ],
                        ["Status", subscriptionData.stripeSubscription.status],
                        [
                          "Quantity",
                          subscriptionData.stripeSubscription.quantity,
                        ],
                        ["Plan", organization?.planId || "—"],
                        [
                          "Period Start",
                          subscriptionData.stripeSubscription.currentPeriodStart
                            ? new Date(
                                subscriptionData.stripeSubscription
                                  .currentPeriodStart * 1000,
                              ).toLocaleDateString()
                            : "—",
                        ],
                        [
                          "Period End",
                          subscriptionData.stripeSubscription.currentPeriodEnd
                            ? new Date(
                                subscriptionData.stripeSubscription
                                  .currentPeriodEnd * 1000,
                              ).toLocaleDateString()
                            : "—",
                        ],
                        [
                          "Cancel at Period End",
                          subscriptionData.stripeSubscription.cancelAtPeriodEnd
                            ? "Yes"
                            : "No",
                        ],
                        [
                          "Product",
                          subscriptionData.stripeSubscription.product?.name ||
                            "—",
                        ],
                        [
                          "Price",
                          subscriptionData.stripeSubscription.price
                            ? `${subscriptionData.stripeSubscription.price.currency?.toUpperCase()} ${(subscriptionData.stripeSubscription.price.unitAmount || 0).toFixed(2)}`
                            : "—",
                        ],
                        [
                          "Discount",
                          subscriptionData.stripeSubscription.discount
                            ?.coupon || "—",
                        ],
                        [
                          "Payment Method",
                          (() => {
                            const pm =
                              subscriptionData.stripeSubscription.paymentMethod;
                            if (!pm) return "—";
                            if (pm.type === "card" && pm.brand && pm.last4)
                              return `${pm.brand} •••• ${pm.last4}`;
                            return pm.type || "—";
                          })(),
                        ],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-gray-500 text-xs">{label}</p>
                          <p className="font-medium text-gray-900 mt-0.5">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                    {subscriptionData.stripeSubscription.invoices?.length >
                      0 && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-3 text-sm">
                          Invoices
                        </h4>
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {[
                                "Invoice #",
                                "Date",
                                "Amount",
                                "Status",
                                "PDF",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="px-3 py-2 text-left font-medium text-gray-500 uppercase"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {subscriptionData.stripeSubscription.invoices.map(
                              (inv) => (
                                <tr key={inv.id}>
                                  <td className="px-3 py-2 font-mono text-gray-700">
                                    {inv.number}
                                  </td>
                                  <td className="px-3 py-2 text-gray-500">
                                    {new Date(
                                      inv.date * 1000,
                                    ).toLocaleDateString()}
                                  </td>
                                  <td className="px-3 py-2 font-medium">
                                    {inv.currency?.toUpperCase()}{" "}
                                    {inv.amount?.toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${inv.status === "paid" ? "bg-green-100 text-green-700" : inv.status === "open" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}
                                    >
                                      {inv.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    {inv.invoicePdf && (
                                      <a
                                        href={inv.invoicePdf}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary hover:underline"
                                      >
                                        PDF
                                      </a>
                                    )}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    No Stripe subscription
                  </p>
                )}
              </div>

              {/* Razorpay */}
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Razorpay</h3>
                {subscriptionData?.razorpaySubscription?.error ? (
                  <p className="text-sm text-red-600">
                    {subscriptionData.razorpaySubscription.error}
                  </p>
                ) : subscriptionData?.razorpaySubscription ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                      {[
                        [
                          "Subscription ID",
                          subscriptionData.razorpaySubscription.id,
                        ],
                        [
                          "Status",
                          subscriptionData.razorpaySubscription.status,
                        ],
                        [
                          "Quantity",
                          subscriptionData.razorpaySubscription.quantity,
                        ],
                        [
                          "Plan ID",
                          subscriptionData.razorpaySubscription.planId,
                        ],
                        [
                          "Period Start",
                          subscriptionData.razorpaySubscription.currentStart
                            ? new Date(
                                subscriptionData.razorpaySubscription
                                  .currentStart * 1000,
                              ).toLocaleDateString()
                            : "—",
                        ],
                        [
                          "Period End",
                          subscriptionData.razorpaySubscription.currentEnd
                            ? new Date(
                                subscriptionData.razorpaySubscription
                                  .currentEnd * 1000,
                              ).toLocaleDateString()
                            : "—",
                        ],
                        [
                          "Payment Method",
                          (() => {
                            const pm =
                              subscriptionData.razorpaySubscription
                                .paymentMethod;
                            if (!pm) return "—";
                            if (pm.type === "card" && pm.brand && pm.last4)
                              return `${pm.brand} •••• ${pm.last4}`;
                            return (pm.type || "—").replace(/_/g, " ");
                          })(),
                        ],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-gray-500 text-xs">{label}</p>
                          <p className="font-medium text-gray-900 mt-0.5">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                    {subscriptionData.razorpaySubscription.invoices?.length >
                      0 && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-3 text-sm">
                          Invoices
                        </h4>
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {[
                                "Invoice #",
                                "Date",
                                "Amount (₹)",
                                "Status",
                                "Link",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="px-3 py-2 text-left font-medium text-gray-500 uppercase"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {subscriptionData.razorpaySubscription.invoices.map(
                              (inv) => (
                                <tr key={inv.id}>
                                  <td className="px-3 py-2 font-mono text-gray-700">
                                    {inv.invoiceNumber || inv.id}
                                  </td>
                                  <td className="px-3 py-2 text-gray-500">
                                    {inv.date
                                      ? new Date(
                                          typeof inv.date === "number"
                                            ? inv.date * 1000
                                            : inv.date,
                                        ).toLocaleDateString()
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-2 font-medium">
                                    {typeof inv.amount === "number"
                                      ? inv.amount.toFixed(2)
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                                    >
                                      {inv.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    {inv.shortUrl && (
                                      <a
                                        href={inv.shortUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary hover:underline"
                                      >
                                        Open
                                      </a>
                                    )}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    No Razorpay subscription
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === "Activity" && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">
            Subscription Activity
          </h3>
          {activities.length === 0 ? (
            <p className="text-gray-400 text-center py-8">
              No activity recorded
            </p>
          ) : (
            <div className="space-y-3">
              {activities.map((act) => (
                <div
                  key={act._id}
                  className="flex items-start gap-3 py-3 border-b last:border-0"
                >
                  <span className="text-lg mt-0.5">
                    {ACTIVITY_ICONS[act.activityType] || "📌"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {act.description}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded font-medium ${act.status === "SUCCESS" ? "bg-green-100 text-green-700" : act.status === "FAILED" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}
                      >
                        {act.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(act.createdAt).toLocaleString()}
                      </span>
                      {act.triggeredBy && (
                        <span className="text-xs text-gray-400">
                          by {act.triggeredBy.name || act.triggeredBy.email}
                        </span>
                      )}
                    </div>
                  </div>
                  {act.amount && (
                    <span className="text-sm font-medium text-gray-700">
                      {act.currency?.toUpperCase()} {act.amount.toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal — full planMeta control (rendered via portal) */}
      {editMode &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8">
              <div className="p-6 max-h-[85vh] overflow-y-auto">
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  Edit Organization
                </h2>

                {/* Subscription (read-only) */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Subscription (read-only)
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Name
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.name}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Plan ID
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.planId || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Subscription Type
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.subscriptionType || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Payment Country
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.payment_country || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Payment Gateway
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.payment_gateway || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">
                        Subscribed
                      </label>
                      <p className="font-medium text-gray-800">
                        {editForm.isSubscribed ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* planMeta — Numeric limits */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Plan Limits (−1 = unlimited)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: "USER_CREATION", label: "Users" },
                      { key: "SIGN_REQUESTS", label: "Sign Requests" },
                      { key: "TEMPLATE_CREATION", label: "Templates" },
                      { key: "ALLOWED_COMPANIES", label: "Max Companies" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          {label}
                        </label>
                        <input
                          type="number"
                          value={editForm.planMeta?.[key] ?? ""}
                          onChange={(e) => {
                            const v =
                              e.target.value === ""
                                ? ""
                                : parseInt(e.target.value, 10);
                            setEditForm((f) => ({
                              ...f,
                              planMeta: {
                                ...f.planMeta,
                                [key]: v === "" ? -1 : v,
                              },
                            }));
                          }}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                          placeholder="-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* planMeta — Boolean features */}
                <div className="space-y-3 mb-6">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    Features
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                    {[
                      "BULK_SEND",
                      "REMINDERS",
                      "MULTI_DEVICE_ACCESS",
                      "SEQUENTIAL_SIGNING",
                      "ROLE_CONTROL",
                      "ATTACHMENT_REQUESTS",
                      "DEDICATED_SUPPORT",
                      "OTP_VERIFICATION",
                    ].map((key) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`pm_${key}`}
                          checked={!!editForm.planMeta?.[key]}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              planMeta: {
                                ...f.planMeta,
                                [key]: e.target.checked,
                              },
                            }))
                          }
                          className="accent-primary"
                        />
                        <label
                          htmlFor={`pm_${key}`}
                          className="text-sm text-gray-700 select-none"
                        >
                          {key.replace(/_/g, " ")}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 text-sm text-gray-700 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const payload = {
                        planMeta: {
                          ...organization.planMeta,
                          USER_CREATION: editForm.planMeta.USER_CREATION ?? -1,
                          SIGN_REQUESTS: editForm.planMeta.SIGN_REQUESTS ?? 0,
                          TEMPLATE_CREATION:
                            editForm.planMeta.TEMPLATE_CREATION ?? -1,
                          ALLOWED_COMPANIES:
                            editForm.planMeta.ALLOWED_COMPANIES ?? 1,
                          BULK_SEND: editForm.planMeta.BULK_SEND ?? false,
                          REMINDERS: editForm.planMeta.REMINDERS ?? false,
                          MULTI_DEVICE_ACCESS:
                            editForm.planMeta.MULTI_DEVICE_ACCESS ?? false,
                          SEQUENTIAL_SIGNING:
                            editForm.planMeta.SEQUENTIAL_SIGNING ?? false,
                          ROLE_CONTROL: editForm.planMeta.ROLE_CONTROL ?? false,
                          ATTACHMENT_REQUESTS:
                            editForm.planMeta.ATTACHMENT_REQUESTS ?? false,
                          DEDICATED_SUPPORT:
                            editForm.planMeta.DEDICATED_SUPPORT ?? false,
                          OTP_VERIFICATION:
                            editForm.planMeta.OTP_VERIFICATION ?? false,
                        },
                      };
                      updateMutation.mutate(payload);
                    }}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 text-sm text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50"
                  >
                    {updateMutation.isPending ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Add Company to Organization
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={addCompanyForm.name}
                    onChange={(e) =>
                      setAddCompanyForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <PhoneInput
                    value={addCompanyForm.phone}
                    onChange={(v) =>
                      setAddCompanyForm((f) => ({ ...f, phone: v }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={addCompanyForm.address}
                    onChange={(e) =>
                      setAddCompanyForm((f) => ({
                        ...f,
                        address: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5 pt-4 border-t">
                <button
                  onClick={() => setShowAddCompanyModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => addCompanyMutation.mutate(addCompanyForm)}
                  disabled={addCompanyMutation.isPending}
                  className="px-4 py-2 text-sm text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50"
                >
                  {addCompanyMutation.isPending ? "Adding..." : "Add Company"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal - Full screen, rendered via createPortal */}
      {showUpgradeModal &&
        upgradeForm &&
        upgradeTargetPlan &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0">
              <h2 className="text-xl font-bold text-gray-900">
                Upgrade to{" "}
                {upgradeTargetPlan === "BUSINESS_PRO"
                  ? "Business Pro"
                  : "Enterprise"}
              </h2>
              <button
                onClick={() => {
                  if (!upgradePlanMutation.isPending) {
                    setShowUpgradeModal(false);
                    setUpgradeTargetPlan(null);
                    setUpgradeForm(null);
                  }
                }}
                disabled={upgradePlanMutation.isPending}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content - scrollable */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              {/* Loader overlay when upgrading */}
              {upgradePlanMutation.isPending && (
                <div className="fixed inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                  <div className="animate-spin rounded-full h-14 w-14 border-2 border-gray-200 border-t-primary" />
                  <p className="mt-4 text-base font-medium text-gray-700">
                    Upgrading plan...
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Creating custom plan and assigning to organization
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500 mb-6">
                Configure custom plan options. Pricing and limits will be
                applied to this organization.
              </p>

              <div className="max-w-3xl space-y-6">
                {/* Billing cycle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Cycle
                  </label>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
                    {["MONTHLY", "YEARLY"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setUpgradeField("subscriptionType", c)}
                        disabled={upgradePlanMutation.isPending}
                        className={`px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                          upgradeForm.subscriptionType === c
                            ? "bg-primary text-white"
                            : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {c === "MONTHLY" ? "Monthly" : "Yearly"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {upgradeForm.subscriptionType === "MONTHLY"
                      ? `Monthly Price (${organization?.payment_country === "IN" ? "₹" : "$"})`
                      : `Yearly Price (${organization?.payment_country === "IN" ? "₹" : "$"})`}{" "}
                    *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      upgradeForm.subscriptionType === "MONTHLY"
                        ? upgradeForm.monthlyPrice
                        : upgradeForm.yearlyPrice
                    }
                    onChange={(e) =>
                      upgradeForm.subscriptionType === "MONTHLY"
                        ? setUpgradeField("monthlyPrice", e.target.value)
                        : setUpgradeField("yearlyPrice", e.target.value)
                    }
                    disabled={upgradePlanMutation.isPending}
                    className="w-full max-w-xs border border-gray-300 rounded-lg px-4 py-2.5 text-sm disabled:opacity-50"
                    placeholder="e.g. 99"
                  />
                </div>

                {/* Numeric limits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Limits (−1 = unlimited)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Users
                      </label>
                      <input
                        type="number"
                        value={upgradeForm.planMeta.USER_CREATION ?? ""}
                        onChange={(e) =>
                          setUpgradePlanMetaField(
                            "USER_CREATION",
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        disabled={upgradePlanMutation.isPending}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Sign Requests
                      </label>
                      <input
                        type="number"
                        value={upgradeForm.planMeta.SIGN_REQUESTS ?? ""}
                        onChange={(e) =>
                          setUpgradePlanMetaField(
                            "SIGN_REQUESTS",
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        disabled={upgradePlanMutation.isPending}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Templates
                      </label>
                      <input
                        type="number"
                        value={upgradeForm.planMeta.TEMPLATE_CREATION ?? ""}
                        onChange={(e) =>
                          setUpgradePlanMetaField(
                            "TEMPLATE_CREATION",
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        disabled={upgradePlanMutation.isPending}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Max Companies
                      </label>
                      <input
                        type="number"
                        value={upgradeForm.planMeta.ALLOWED_COMPANIES ?? ""}
                        onChange={(e) =>
                          setUpgradePlanMetaField(
                            "ALLOWED_COMPANIES",
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        disabled={upgradePlanMutation.isPending}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Feature flags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                    {[
                      { key: "BULK_SEND", label: "Bulk Send" },
                      { key: "REMINDERS", label: "Reminders" },
                      {
                        key: "MULTI_DEVICE_ACCESS",
                        label: "Multi-Device Access",
                      },
                      {
                        key: "SEQUENTIAL_SIGNING",
                        label: "Sequential Signing",
                      },
                      { key: "ROLE_CONTROL", label: "Role Control" },
                      {
                        key: "ATTACHMENT_REQUESTS",
                        label: "Attachment Requests",
                      },
                      { key: "DEDICATED_SUPPORT", label: "Dedicated Support" },
                      { key: "OTP_VERIFICATION", label: "OTP Verification" },
                      { key: "IS_GROUP_PLAN", label: "Group Plan" },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`upgrade_pm_${key}`}
                          checked={!!upgradeForm.planMeta[key]}
                          onChange={(e) =>
                            setUpgradePlanMetaField(key, e.target.checked)
                          }
                          disabled={upgradePlanMutation.isPending}
                          className="accent-primary disabled:opacity-50"
                        />
                        <label
                          htmlFor={`upgrade_pm_${key}`}
                          className="text-sm text-gray-700 select-none"
                        >
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 shrink-0 bg-gray-50">
              <button
                onClick={() => {
                  if (!upgradePlanMutation.isPending) {
                    setShowUpgradeModal(false);
                    setUpgradeTargetPlan(null);
                    setUpgradeForm(null);
                  }
                }}
                disabled={upgradePlanMutation.isPending}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgradeSubmit}
                disabled={upgradePlanMutation.isPending}
                className="px-5 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {upgradePlanMutation.isPending ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Upgrading…
                  </>
                ) : (
                  "Upgrade"
                )}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OrganizationDetail;
